import { comparer, makeAutoObservable, observable, reaction, runInAction } from "mobx";
import type { ColumnFiltersState, SortingState } from "@tanstack/react-table";
import type { RootStore } from "./root.store";
import {
  isColumnFiltersState,
  isSortingState,
  readTablePref,
  writeTablePref,
  type TablePrefsKey,
} from "@/components/data-table/table-prefs-storage";
import { tablePrefsEnabledNow } from "@/components/data-table/table-prefs-gate";
import { pollExportStatus } from "@/components/data-table/use-csv-export";
import { runBulkAction, runBulkDelete } from "@/components/data-table/run-bulk-delete";
import { type AnalyticsSource, track } from "@/lib/analytics";
import { QUERY_CACHE_FLAG } from "@/lib/launchdarkly/flags";
import { isCancelledError } from "@tanstack/react-query";
import { getQueryClient, peekQueryClient } from "@/queries/client";
import { dropOnDenial } from "@/queries/invalidation";
import { FRESHNESS } from "@/queries/policies";
import {
  projectBankKey,
  projectBankQuery,
  projectDetailKey,
  projectDetailQuery,
} from "@/queries/projects";
import { scopeFor, ScopeNotReadyError } from "@/queries/scope";
import type {
  TBulkArchiveProjectsResult,
  TBulkAssignLeadsResult,
  TBulkSetProjectStatusResult,
  TPartialProject,
  TProject,
  TProjectsBoardGroupBy,
  TWorkspaceProjectRow,
  TWorkspaceProjectsListQuery,
  TWorkspaceProjectsPage,
} from "@/types/project";
import type {
  TBulkDeleteResult,
  TBulkOperationStatus,
  TBulkSelection,
} from "@/types/bulk-selection";
import type { TProjectFilters } from "@/types/project-filter";
import type { ProjectGroupBy } from "@/components/projects/project-group-rows";
import type { ProjectViewFilterState } from "@/helpers/project-view.helper";

const PROJECTS_DEFAULT_PER_PAGE = 50;
// The longest a pushed hint's re-read of the bank waits (`markBankChangedElsewhere`).
const BANK_REREAD_JITTER_MS = 2_000;

/**
 * Pages the Board, Timeline and Calendar layouts fetch on their own before they
 * stop and offer "Load more" (see `loadRemainingPages`). Those layouts have no
 * scroll sentinel, so without this they drew the first page and nothing said
 * the rest existed. Ten pages is 500 projects: ten sequential reads, each
 * inside the 1s read budget, so a 500-project workspace settles in a few
 * seconds behind a visible "Loading projects… X of Y" line. A workspace with
 * more than that gets told, and asks for the next 500 itself.
 */
export const PROJECTS_LAYOUT_AUTOLOAD_PAGES = 10;

/**
 * localStorage key an e2e spec sets to shrink the list page size, so a spec can
 * cross a page boundary with a handful of its own projects instead of creating
 * fifty. Nothing in the app writes it. The backend still caps `per_page` at 100.
 */
export const PROJECTS_PAGE_SIZE_OVERRIDE_KEY = "cognota:projects-page-size-override";

function projectsListPageSize(): number {
  if (typeof window === "undefined") return PROJECTS_DEFAULT_PER_PAGE;
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(PROJECTS_PAGE_SIZE_OVERRIDE_KEY);
  } catch (err) {
    console.warn("[projects] read page size override failed", err);
    return PROJECTS_DEFAULT_PER_PAGE;
  }
  if (raw === null) return PROJECTS_DEFAULT_PER_PAGE;
  const size = Number(raw);
  return Number.isInteger(size) && size > 0 && size <= PROJECTS_DEFAULT_PER_PAGE
    ? size
    : PROJECTS_DEFAULT_PER_PAGE;
}

/** Must match the `tableId` `routes/workspace/projects.tsx` passes to
 *  `<DataTable>`, so the stored sort/filters share that table's namespace. */
const PROJECTS_TABLE_ID = "projects";

/**
 * Column-filter ids the LIST ENDPOINT does not understand — the projects route
 * narrows rows for these in the browser.
 *
 * It lives here, next to `setColumnFilters`, because the store now owns the
 * whole filter set: it persists both halves under one key and hydrates both
 * before the first fetch. Splitting the rule across the route and the store
 * would let the stored value and the applied value disagree with nothing to
 * catch it.
 *
 * These are deliberately NOT capturable into a saved view (NG-529): they narrow
 * the page already in memory, so the list endpoint never saw them. Persisting
 * one into a view would restore a row set the server cannot reproduce — the
 * first page would look filtered and every page after it would not. A capture
 * helper (`projectsFilterState`) must skip any filter id in this set.
 */
const CLIENT_SIDE_FILTER_IDS: ReadonlySet<string> = new Set(["name", "progress", "openTasks"]);

/** The column-filter shapes a custom-field column can carry (see
 *  `column-filter.tsx`): "multi" for select/multiselect, "dateRange" for date. */
export type TCustomFieldColumnFilter =
  | { type: "multi"; value: string[] }
  | { type: "dateRange"; value: { from: string; to: string } };

function isActiveCustomFieldFilter(filter: TCustomFieldColumnFilter): boolean {
  if (filter.type === "multi") return filter.value.length > 0;
  return Boolean(filter.value.from || filter.value.to);
}

/** Serialize active custom-field column filters to the backend's repeatable
 *  `custom_field` values: `<field_id>:<value>` per picked option, and
 *  `<field_id>:gte:<from>` / `<field_id>:lte:<to>` for date bounds. */
function serializeCustomFieldFilters(
  entries: Iterable<[columnId: string, filter: TCustomFieldColumnFilter]>,
): string[] {
  const out: string[] = [];
  for (const [columnId, filter] of entries) {
    const fieldId = columnId.replace(/^cf:/, "");
    if (filter.type === "multi") {
      for (const value of filter.value) out.push(`${fieldId}:${value}`);
    } else {
      if (filter.value.from) out.push(`${fieldId}:gte:${filter.value.from}`);
      if (filter.value.to) out.push(`${fieldId}:lte:${filter.value.to}`);
    }
  }
  return out;
}

interface ProjectPageState {
  results: TWorkspaceProjectRow[];
  nextCursor: string | null;
  prevCursor: string | null;
  hasNext: boolean;
  hasPrev: boolean;
  totalCount: number;
  totalPages: number;
}

const EMPTY_PROJECT_PAGE_STATE: ProjectPageState = {
  results: [],
  nextCursor: null,
  prevCursor: null,
  hasNext: false,
  hasPrev: false,
  totalCount: 0,
  totalPages: 0,
};

function projectsEnvelopeToPageState(page: TWorkspaceProjectsPage): ProjectPageState {
  return {
    results: page.results,
    nextCursor: page.next_page_results ? page.next_cursor : null,
    prevCursor: page.prev_page_results ? page.prev_cursor : null,
    hasNext: page.next_page_results,
    hasPrev: page.prev_page_results,
    totalCount: page.total_count,
    totalPages: page.total_pages,
  };
}

type ProjectRow = TProject | TPartialProject;

/**
 * The keys of a project write whose value differs from the row before it:
 * `project_updated`'s `fields`. A row the cache does not hold counts every key
 * it sends. Values compare by their JSON form, which covers the scalars, id
 * lists and small objects a project PATCH carries.
 */
function changedProjectFields(before: ProjectRow | undefined, data: Partial<TProject>): string[] {
  return (Object.keys(data) as (keyof TProject)[]).filter(
    (key) =>
      !before ||
      JSON.stringify((before as Partial<TProject>)[key] ?? null) !==
        JSON.stringify(data[key] ?? null),
  );
}

/**
 * Product analytics for one successful project write (NG-2055). `project_updated`
 * fires for every write that changed something, and a status or lead change
 * also fires its own event. A one-for-one lead swap is a single
 * `project_lead_assigned` carrying `from_member_id`. Props are ids only.
 */
function trackProjectWrite(
  projectId: string,
  before: ProjectRow | undefined,
  data: Partial<TProject>,
  source: AnalyticsSource,
): void {
  const fields = changedProjectFields(before, data);
  if (fields.length === 0) return;
  track("project_updated", { project_id: projectId, fields, source });
  if (fields.includes("state")) {
    track("project_status_changed", {
      project_id: projectId,
      from: before?.state ?? null,
      to: data.state ?? null,
      source,
    });
  }
  if (fields.includes("project_leads")) {
    const previous = before?.project_leads ?? [];
    const next = data.project_leads ?? [];
    const added = next.filter((id) => !previous.includes(id));
    const removed = previous.filter((id) => !next.includes(id));
    if (added.length === 1 && removed.length === 1) {
      track("project_lead_assigned", {
        project_id: projectId,
        member_id: added[0],
        from_member_id: removed[0],
        source,
      });
      return;
    }
    for (const memberId of added) {
      track("project_lead_assigned", { project_id: projectId, member_id: memberId, source });
    }
    for (const memberId of removed) {
      track("project_lead_unassigned", { project_id: projectId, member_id: memberId, source });
    }
  }
}

/** How many projects a bulk action changed, whichever result shape came back. */
function bulkChangedCount(result: object): number {
  const r = result as Record<string, unknown>;
  const count = r.deleted ?? r.archived ?? r.updated ?? r.processed;
  return typeof count === "number" ? count : 0;
}

export class ProjectStore {
  projectsByWorkspace = observable.map<string, TPartialProject[]>();
  projectDetailMap = observable.map<string, TProject>();
  /**
   * Per-project counter, bumped by every write that settles against the detail
   * cache — each mutation, and the access-denial eviction. `fetchProjectDetail` samples it before its request and
   * refuses to write if it moved, so an in-flight GET cannot land on top of a
   * newer write.
   *
   * Without it the cache was last-response-wins: a detail GET that started
   * before a PATCH but resolved after it replaced the whole entry with the
   * pre-write payload. That read as "my change did not apply" — or, changing a
   * field that had been empty, as "it cleared itself" — and worked on the
   * second attempt, because by then no stale GET was in flight. Every project
   * tab's `clientLoader` issues one of these GETs, so editing while a
   * navigation was still resolving was enough to hit it.
   *
   * Deliberately not observable: it is bookkeeping about requests, and making
   * it observable would re-render every reader of the map on each write.
   */
  private detailWriteSeq = new Map<string, number>();
  /** In-flight detail GETs, keyed `<slug>:<projectId>`, with the
   *  `detailWriteSeq` value they started under. Same reason as the counter
   *  above for not being observable: it is bookkeeping about requests, and
   *  observing it would re-render every reader of the detail map. */
  private detailInFlight = new Map<string, { seq: number; promise: Promise<TProject> }>();
  isLoading = false;
  isUpdating = false;

  pageBySlug = observable.map<string, ProjectPageState>();
  cursorBySlug = observable.map<string, string | null>();

  searchQuery = "";
  stateFilter = observable.array<string>([], { deep: false });
  // Managed Project Category value ids (NG-420). Single-valued FK server-side,
  // so a multi-pick is an OR over ids — same shape as `stateFilter`.
  categoryFilter = observable.array<string>([], { deep: false });
  leadFilter = observable.array<string>([], { deep: false });
  deliveryTeamsFilter = observable.array<string>([], { deep: false });
  // Home team ids (NG-2163). An OR over ids matched against each project's
  // scalar `home_team`, never its roster: a delivery team that is not the Home
  // does not match. Only the flag-gated Home team column and facet set it.
  homeTeamFilter = observable.array<string>([], { deep: false });
  clientTeamsFilter = observable.array<string>([], { deep: false });
  programsFilter = observable.array<string>([], { deep: false });
  // Linked-goal ids (NG-619). Same OR-over-ids shape as `programsFilter`; the
  // server joins the ACTIVE link rows, so a goal a project was unlinked from
  // stops matching.
  goalsFilter = observable.array<string>([], { deep: false });
  // Process ids, and `ProcessStage` ids matched against each project's DERIVED
  // `current_stage` (NG-713). Same OR-over-ids shape as `stateFilter`.
  processFilter = observable.array<string>([], { deep: false });
  stageFilter = observable.array<string>([], { deep: false });
  archivedFilter = false;
  // Active custom-field column filters, keyed by column id ("cf:<field_id>").
  // Values are the raw column-filter shapes ("multi" for select/multiselect,
  // "dateRange" for date) — serialized to the backend's repeatable
  // `custom_field=<field_id>:<value>` wire format at query-build time.
  customFieldFilters = observable.map<string, TCustomFieldColumnFilter>();
  /**
   * The client-side filters (`CLIENT_SIDE_FILTER_IDS`), held verbatim as full
   * entries — the route's row predicate switches on `id`, so bare values would
   * not tell it which filter is which. Never sent to the API; the store holds
   * them so ONE owner persists the whole set the user sees.
   */
  clientColumnFilters = observable.array<ColumnFiltersState[number]>([], { deep: false });

  /** Board display prefs (NG-713). NOT list filters: the board bins the loaded
   *  rows client-side, so neither reaches the server and neither belongs in
   *  `clearListFilters`'s invariant (which covers what `buildProjectsListQuery`
   *  sends). Held here rather than in component state so a layout switch and a
   *  remount keep the pick, exactly like `sorting`. */
  boardGroupBy: TProjectsBoardGroupBy = "status";
  boardProcessId: string | null = null;

  // Per-workspace "can the caller create a project at all?" verdict, backing the
  // "New Project" button gate for callers without a workspace-scope
  // project.create grant (NG-2176). `undefined` = not yet answered (the button
  // stays hidden to avoid a flash); a boolean is the cached probe result. Same
  // shape as `ProgramsStore.canCreateByWorkspace` (#3206).
  canCreateByWorkspace = observable.map<string, boolean>();

  sorting: SortingState = [];
  // Server-side group-by token; "none" disables grouping. Single-valued
  // dimensions ("status", "category") group one row per project; the M2M ones
  // duplicate a project into each of its groups (#1437).
  groupBy: ProjectGroupBy = "none";
  isLoadingMore = false;
  // How many rows the Board / Timeline / Calendar layouts load on their own
  // before offering "Load more". Reset to one batch whenever a first-page load
  // starts, so a new filter or search does not inherit an earlier "Load more".
  layoutRowLimit = PROJECTS_LAYOUT_AUTOLOAD_PAGES * PROJECTS_DEFAULT_PER_PAGE;
  // Last list-load failure, surfaced to the DataTable `error` prop. Cleared
  // when a (re)load starts; set when loadPage/loadMore reject.
  loadError: Error | null = null;

  // Per-slug in-flight guard for `ensureProjects` — the full workspace bank,
  // not a page. Deliberately not observable: bookkeeping about a read.
  private inflightAllProjects = new Map<string, Promise<TPartialProject[]>>();
  // Per-slug count of writes that changed the workspace's project list (a row
  // edited, added or removed, or a bulk change the store does not patch). A
  // bank read with the `query-cache` flag on remembers it at the start, and
  // projects its answer only if no write moved it meanwhile (`markBankWrite`).
  // Not observable, for the same reason as `detailWriteSeq`.
  private bankWriteSeq = new Map<string, number>();
  // The slugs whose `projectsByWorkspace` entry holds EVERY project, not a page
  // of them.
  //
  // It is needed because that map has two kinds of writer. `fetchProjects`
  // walks the whole endpoint and stores the complete bank; `loadPage` and
  // `loadMore` store the paginated TABLE's rows in the same slot — so after a
  // visit to the Projects list the entry is one page, and a reader asking for
  // "this workspace's projects" would get that page and never know. Single-row
  // writers (create, delete, a merge from the picker search) preserve
  // completeness and deliberately leave this alone.
  private fullyLoadedWorkspaces = new Set<string>();
  // Open pickers that render the bank, per workspace, and each workspace's
  // pending hint-driven re-read (`holdBankOpen`, `markBankChangedElsewhere`).
  private bankHolds = new Map<string, number>();
  private bankRereadTimers = new Map<string, number>();
  private inflightPage = new Map<string, Promise<ProjectPageState>>();
  // Slugs whose LATEST list load is still in flight. Set when a load starts and
  // cleared in the same action that clears `isLoading`, by the latest load only,
  // so the layouts' walk (restarted on that flip) never sees it stale (NG-2676).
  private pageLoadingSlugs = new Set<string>();
  private inflightLoadMore = new Map<string, Promise<void>>();
  // `loadRemainingPages` bookkeeping: the running loop per slug, and the slugs
  // whose layout still wants it to run (cleared by `stopLoadingRemainingPages`).
  private inflightRemaining = new Map<string, Promise<void>>();
  private remainingWanted = new Set<string>();
  // Slugs whose latest first-page load failed, so `retryLayoutLoad` reloads that
  // page rather than walking on from rows a different query left behind.
  private firstPageFailed = new Set<string>();
  // Per-slug monotonic load token for latest-wins. A slug can have an unfiltered
  // loader load and a filtered search load in flight at once (distinct dedup keys
  // below), so each load is tagged and only the response that is still the latest
  // is applied; a slow earlier load can no longer clobber a newer one. (#1809)
  private loadTokenBySlug = new Map<string, number>();
  // The workspace whose stored list prefs are currently loaded, or null.
  //
  // A single slug rather than a set of every slug seen: the state it guards
  // (`sorting`, the filter observables) is GLOBAL to the store, so
  // once-per-slug-per-session would let `/acme` → `/other` → `/acme` leave
  // acme's list running on other's workspace-local entity ids — an empty table
  // with chips pointing at rows that do not exist. Comparing against the
  // current slug keeps same-slug hydration idempotent while making a return
  // visit re-read the returning workspace's key.
  //
  // Deliberately not observable: bookkeeping about a read, nothing renders it.
  private hydratedSlug: string | null = null;
  // True only while `hydrateListPrefs` is applying a value it just READ, so the
  // persistence reactions below don't write it straight back.
  private hydratingListPrefs = false;

  // Filter/sort state (local to this session, not persisted)
  filters: TProjectFilters = {
    search: "",
    privacy: null,
    sortBy: "sort_order",
    sortOrder: "asc",
  };

  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(
      this,
      {
        rootStore: false,
        detailWriteSeq: false,
        detailInFlight: false,
        inflightAllProjects: false,
        bankWriteSeq: false,
        fullyLoadedWorkspaces: false,
        bankHolds: false,
        bankRereadTimers: false,
        inflightPage: false,
        pageLoadingSlugs: false,
        inflightLoadMore: false,
        inflightRemaining: false,
        remainingWanted: false,
        firstPageFailed: false,
        loadTokenBySlug: false,
        hydratedSlug: false,
        hydratingListPrefs: false,
        // NOT an action, deliberately — see `hydrateListPrefs`. MobX flushes
        // pending reactions when the OUTERMOST action ends, so if this method
        // were an action its own `runInAction` would not flush them and the
        // write-back guard inside it would already have been cleared by the
        // time the persistence reactions ran.
        hydrateListPrefs: false,
      } as never,
      { autoBind: true },
    );
    // Opting out of the action wrapping above also opts out of `autoBind`, and
    // callers here routinely lift store methods off the instance
    // (`const loadPage = projectStore.loadPage`). Bind it back so that stays safe.
    this.hydrateListPrefs = this.hydrateListPrefs.bind(this);
    this.watchListPrefs();
  }

  /**
   * The ONE place the list sort + filters are persisted.
   *
   * Deliberately a reaction over the DERIVED state rather than a write in each
   * setter. Nine mutators write the state these two keys hold —
   * `setColumnFilters`, `setSearchQuery`, the six facet setters and
   * `setArchivedFilter` — and wiring the write to just one of them is exactly
   * how storage came to diverge from state: clearing the search box left the
   * stored `search` entry behind, so a reload resurrected it. Reacting to the
   * derived value covers every mutator, including the next one someone adds.
   *
   * The filter expression is the SAME union the route feeds to `<DataTable>` —
   * server-derived entries plus the client half — so what gets stored is the
   * whole set the user sees, never a truncated server half.
   *
   * No disposer: `ProjectStore` lives for the lifetime of the app, so these
   * reactions are never torn down.
   */
  private watchListPrefs(): void {
    reaction(
      () => [...this.columnFilters, ...this.clientColumnFilters],
      (filters) => this.persistListPref("columnFilters", filters),
      // Structural, so re-applying an equal set is not a write.
      { equals: comparer.structural },
    );
    reaction(
      () => [...this.sorting],
      (sorting) => this.persistListPref("sorting", sorting),
      { equals: comparer.structural },
    );
  }

  get pagination(): ProjectPageState {
    const slug = this.rootStore.router.workspaceSlug;
    if (!slug) return EMPTY_PROJECT_PAGE_STATE;
    return this.pageBySlug.get(slug) ?? EMPTY_PROJECT_PAGE_STATE;
  }

  /**
   * The current list filters/sort/group-by, in the same shape the list
   * endpoint and CSV export already scope to (`buildProjectsListQuery`) —
   * exposed publicly so a "select all N matching" bulk selection (mode
   * `"all"`) can reuse it as `selection.filters` without duplicating the
   * filter-serialization logic.
   */
  get currentListQuery(): TWorkspaceProjectsListQuery {
    return this.buildProjectsListQuery(null);
  }

  setSearchQuery(value: string): void {
    this.searchQuery = value;
  }
  setStateFilter(ids: Iterable<string>): void {
    this.stateFilter.replace([...ids]);
  }
  setCategoryFilter(ids: Iterable<string>): void {
    this.categoryFilter.replace([...ids]);
  }
  setLeadFilter(ids: Iterable<string>): void {
    this.leadFilter.replace([...ids]);
  }
  setDeliveryTeamsFilter(ids: Iterable<string>): void {
    this.deliveryTeamsFilter.replace([...ids]);
  }
  setHomeTeamFilter(ids: Iterable<string>): void {
    this.homeTeamFilter.replace([...ids]);
  }
  setClientTeamsFilter(ids: Iterable<string>): void {
    this.clientTeamsFilter.replace([...ids]);
  }
  setProgramsFilter(ids: Iterable<string>): void {
    this.programsFilter.replace([...ids]);
  }
  setGoalsFilter(ids: Iterable<string>): void {
    this.goalsFilter.replace([...ids]);
  }
  setProcessFilter(ids: Iterable<string>): void {
    this.processFilter.replace([...ids]);
  }
  setStageFilter(ids: Iterable<string>): void {
    this.stageFilter.replace([...ids]);
  }
  setBoardGroupBy(next: TProjectsBoardGroupBy): void {
    this.boardGroupBy = next;
  }
  setBoardProcessId(next: string | null): void {
    this.boardProcessId = next;
  }
  setArchivedFilter(value: boolean): void {
    this.archivedFilter = value;
  }
  /**
   * The current projects filter set, in the shape a saved view stores.
   *
   * The read counterpart of `hydrateListFilters`; both live here so the field list
   * stays in one file rather than one copy per consumer.
   */
  get projectsFilterState(): ProjectViewFilterState {
    return {
      search: this.searchQuery,
      stateIds: [...this.stateFilter],
      categoryIds: [...this.categoryFilter],
      leadIds: [...this.leadFilter],
      deliveryTeamIds: [...this.deliveryTeamsFilter],
      homeTeamIds: [...this.homeTeamFilter],
      clientTeamIds: [...this.clientTeamsFilter],
      programIds: [...this.programsFilter],
      goalIds: [...this.goalsFilter],
      processIds: [...this.processFilter],
      stageIds: [...this.stageFilter],
      archived: this.archivedFilter,
      groupBy: this.groupBy,
    };
  }

  /**
   * REPLACE the whole projects filter set with a saved view's state.
   *
   * A full replace, not a merge, and that is the point: this store is a singleton
   * shared by the plain Projects list and every project-view page, so a merge would
   * let one surface's filters survive into the next. Clearing first is what makes
   * it a replace, and it lives here rather than at the call site so every caller
   * gets it — the same ownership `IntakeStore.hydrateSubmissionsFilters` has.
   *
   * INVARIANT: keep this covering every field `clearListFilters` resets — a field
   * cleared there but not assigned here silently drops that filter when a view
   * opens, and one assigned here but not cleared there leaks into the next surface.
   *
   * Does NOT fetch — the caller decides when to load, so a mount can seed and load
   * once rather than twice.
   */
  hydrateListFilters(next: ProjectViewFilterState): void {
    // Dropping `hydratedSlug` is what keeps a view's filters out of the plain
    // list's persisted prefs (NG-517 review). The persistence reactions flush
    // when the OUTERMOST action ends and read the slug then — so nulling it
    // here means this seed, and every filter/sort change made while the view
    // owns the store, has no key to write under. The next plain-list mount
    // re-arms persistence via `hydrateListPrefs`, whose same-slug guard the
    // null also releases, so it re-reads the stored set this method displaced.
    this.hydratedSlug = null;
    this.clearListFilters();
    this.searchQuery = next.search;
    this.stateFilter.replace(next.stateIds);
    this.categoryFilter.replace(next.categoryIds);
    this.leadFilter.replace(next.leadIds);
    this.deliveryTeamsFilter.replace(next.deliveryTeamIds);
    this.homeTeamFilter.replace(next.homeTeamIds);
    this.clientTeamsFilter.replace(next.clientTeamIds);
    this.programsFilter.replace(next.programIds);
    this.goalsFilter.replace(next.goalIds);
    this.processFilter.replace(next.processIds);
    this.stageFilter.replace(next.stageIds);
    this.archivedFilter = next.archived;
    this.groupBy = next.groupBy;
  }

  /**
   * Reset every list filter this store holds.
   *
   * INVARIANT: keep this covering every field `hydrateListFilters` assigns, and
   * every field `buildProjectsListQuery` sends. `groupBy` and `customFieldFilters`
   * are both in that set: they reach the server (`group_by`, `custom_field`), so
   * leaving them set here is one surface's narrowing surviving into the next.
   */
  clearListFilters(): void {
    this.searchQuery = "";
    this.clientColumnFilters.clear();
    this.stateFilter.clear();
    this.categoryFilter.clear();
    this.leadFilter.clear();
    this.deliveryTeamsFilter.clear();
    this.homeTeamFilter.clear();
    this.clientTeamsFilter.clear();
    this.programsFilter.clear();
    this.goalsFilter.clear();
    this.processFilter.clear();
    this.stageFilter.clear();
    this.archivedFilter = false;
    this.groupBy = "none";
    this.customFieldFilters.clear();
  }

  setSorting(next: SortingState, slug: string): void {
    this.sorting = next;
    // Persistence is handled by `watchListPrefs`, not here — see why there.
    // Reset cursor and reload from page 1.
    this.cursorBySlug.set(slug, null);
    void this.loadPage(slug, null);
  }

  setGroupBy(next: ProjectGroupBy, slug: string): void {
    this.groupBy = next;
    // Group-by changes the server ordering, so reset the cursor and reload.
    this.cursorBySlug.set(slug, null);
    void this.loadPage(slug, null);
  }

  get columnFilters(): ColumnFiltersState {
    const out: ColumnFiltersState = [];
    if (this.searchQuery.trim()) {
      out.push({ id: "search", value: { type: "text", value: this.searchQuery } });
    }
    if (this.stateFilter.length > 0) {
      // id must match the "status" column id in projects-columns.tsx — the
      // primitive keys filters/chips by column id, not the BE field name.
      out.push({ id: "status", value: { type: "multi", value: [...this.stateFilter] } });
    }
    if (this.categoryFilter.length > 0) {
      out.push({ id: "category", value: { type: "multi", value: [...this.categoryFilter] } });
    }
    if (this.leadFilter.length > 0) {
      out.push({ id: "lead", value: { type: "multi", value: [...this.leadFilter] } });
    }
    if (this.deliveryTeamsFilter.length > 0) {
      out.push({
        id: "deliveryTeams",
        value: { type: "multi", value: [...this.deliveryTeamsFilter] },
      });
    }
    if (this.homeTeamFilter.length > 0) {
      out.push({ id: "homeTeam", value: { type: "multi", value: [...this.homeTeamFilter] } });
    }
    if (this.clientTeamsFilter.length > 0) {
      out.push({ id: "clientTeams", value: { type: "multi", value: [...this.clientTeamsFilter] } });
    }
    if (this.programsFilter.length > 0) {
      out.push({ id: "programs", value: { type: "multi", value: [...this.programsFilter] } });
    }
    if (this.goalsFilter.length > 0) {
      out.push({ id: "goals", value: { type: "multi", value: [...this.goalsFilter] } });
    }
    // Only the stage entry: there IS a "stage" column (NG-713), and there is no
    // `process` column — emitting an id the table has no column for would let
    // the table's own `onColumnFiltersChange` drop it on every list render.
    if (this.stageFilter.length > 0) {
      out.push({ id: "stage", value: { type: "multi", value: [...this.stageFilter] } });
    }
    if (this.archivedFilter) {
      out.push({ id: "archived", value: { type: "text", value: "true" } });
    }
    for (const [id, value] of this.customFieldFilters) {
      out.push({ id, value });
    }
    return out;
  }

  private listPrefsKey(slug: string): TablePrefsKey {
    // `|| null` is a second line of defence — `persistListPref` and
    // `hydrateListPrefs` both bail on a falsy slug, so this should be
    // unreachable. An empty-string scope collapses to the UNSCOPED key, which
    // both collides with the legacy column-prefs namespace and orphans the
    // value the moment a real slug arrives.
    return { tableId: PROJECTS_TABLE_ID, scope: slug || null };
  }

  private persistListPref(
    slice: "sorting" | "columnFilters",
    value: SortingState | ColumnFiltersState,
  ): void {
    // Behind `persisted-table-prefs` (NG-517). This store reaches
    // `writeTablePref` directly rather than through `useTableSorting` /
    // `useTableColumnFilters`, so the hook-side gate does not cover it — without
    // this check the three busiest lists would keep persisting with the flag off.
    if (!tablePrefsEnabledNow(this.rootStore.featureFlags)) return;
    // Hydration applies a value it just READ; writing it back would be a
    // pointless write and would misreport who last wrote the key.
    if (this.hydratingListPrefs) return;
    // The slug hydration READ this state under — deliberately not
    // `rootStore.router.workspaceSlug`, which `RouterSync` sets from an effect
    // (`router-sync.tsx`), so during a `clientLoader` it still holds the
    // OUTGOING workspace. Reading it here made correctness depend on `RouterSync`
    // rendering before `<Outlet />` in `root.tsx` — reorder those siblings, or
    // put `RouterSync` under a suspense boundary, and one workspace's filters
    // start filing under another's key. `hydrateListPrefs` runs before any
    // mutator can, and it takes its slug from the route's own params, which
    // cannot lag. Matches `intake.store.ts` and `report.store.ts`.
    const slug = this.hydratedSlug;
    // No workspace means no key worth writing: an empty-string scope collapses
    // to the UNSCOPED bucket, which would be orphaned once a real slug arrives.
    if (!slug) return;
    writeTablePref(this.listPrefsKey(slug), slice, value);
  }

  /**
   * Read this workspace's stored sort + filters into the store.
   *
   * Called from the route's `clientLoader`, which React Router runs BEFORE the
   * first render — so the load effect's first run already builds a query
   * carrying them and the page issues ONE request instead of an unfiltered one
   * followed by a filtered one. Hydrating from an effect cannot work here: that
   * effect is keyed on signatures of the very observables hydration writes, so
   * it would re-fire itself.
   *
   * Idempotent while the slug does not change, so a re-entry into the same
   * workspace cannot replay stale storage over a change the user made since.
   * A DIFFERENT slug always re-hydrates — see `hydratedSlug` for why that is
   * not optional.
   */
  hydrateListPrefs(slug: string): void {
    if (!slug || this.hydratedSlug === slug) return;
    this.hydratedSlug = slug;
    const key = this.listPrefsKey(slug);
    // Behind `persisted-table-prefs` (NG-517). Gated at the READS rather than
    // with an early return, because `applyColumnFilters` below REPLACES the
    // whole filter set — so applying the empty arrays is what resets a store
    // still holding another workspace's filters, or a saved view's. An early
    // return skipped that reset, and the plain Projects list then queried with
    // filters the user could not see and had not chosen (review, NG-517).
    // Restoring nothing is exactly the pre-NG-517 behaviour: `sorting` starts
    // `[]` on this store, so the empty arrays are the values the reset applied
    // before any of this existed.
    const restore = tablePrefsEnabledNow(this.rootStore.featureFlags);
    // `readTablePref` distinguishes absent from empty, so a user who cleared
    // the sort gets `[]` back rather than a default.
    const sorting = restore ? readTablePref<SortingState>(key, "sorting", [], isSortingState) : [];
    const filters = restore
      ? readTablePref<ColumnFiltersState>(key, "columnFilters", [], isColumnFiltersState)
      : [];
    // The guard has to still be set when the persistence reactions run. MobX
    // flushes them as the OUTERMOST action ends — which is why this method is
    // excluded from `makeAutoObservable`'s action wrapping (see the constructor):
    // that makes the `runInAction` below the outermost action, so the reactions
    // fire inside it, while the guard is up. All observable writes here must
    // therefore stay inside that `runInAction`.
    this.hydratingListPrefs = true;
    try {
      runInAction(() => {
        this.sorting = sorting;
        this.applyColumnFilters(filters);
      });
    } finally {
      this.hydratingListPrefs = false;
    }
  }

  setColumnFilters(next: ColumnFiltersState): void {
    // Persistence is handled by `watchListPrefs`, not here — see why there.
    this.applyColumnFilters(next);
  }

  /** Apply a filter set to the observables. No persistence and no reload, so
   *  hydration can reuse it without writing back what it just read. */
  private applyColumnFilters(next: ColumnFiltersState): void {
    const find = (id: string) =>
      next.find((f) => f.id === id)?.value as
        | { type: "text"; value: string }
        | { type: "multi"; value: string[] }
        | undefined;

    this.searchQuery = (find("search") as { value: string } | undefined)?.value ?? "";
    this.stateFilter.replace((find("status") as { value: string[] } | undefined)?.value ?? []);
    this.categoryFilter.replace((find("category") as { value: string[] } | undefined)?.value ?? []);
    this.leadFilter.replace((find("lead") as { value: string[] } | undefined)?.value ?? []);
    this.deliveryTeamsFilter.replace(
      (find("deliveryTeams") as { value: string[] } | undefined)?.value ?? [],
    );
    // The Home team COLUMN header filter (NG-2163), the same door the Goals line
    // below documents: without it the funnel ticks and narrows nothing.
    this.homeTeamFilter.replace((find("homeTeam") as { value: string[] } | undefined)?.value ?? []);
    this.clientTeamsFilter.replace(
      (find("clientTeams") as { value: string[] } | undefined)?.value ?? [],
    );
    this.programsFilter.replace((find("programs") as { value: string[] } | undefined)?.value ?? []);
    // The Goals COLUMN header filter (NG-619). Without this line the column's
    // funnel opens, ticks and highlights, and narrows nothing — the header
    // filter and the filter facet are two separate doors into the same store
    // field, and only the facet was wired.
    this.goalsFilter.replace((find("goals") as { value: string[] } | undefined)?.value ?? []);
    this.stageFilter.replace((find("stage") as { value: string[] } | undefined)?.value ?? []);
    this.archivedFilter = !!find("archived");
    this.customFieldFilters.replace(
      next
        .filter((f) => f.id.startsWith("cf:"))
        .map((f) => [f.id, f.value as TCustomFieldColumnFilter] as const)
        .filter(([, value]) => isActiveCustomFieldFilter(value)),
    );
    this.clientColumnFilters.replace(next.filter((f) => CLIENT_SIDE_FILTER_IDS.has(f.id)));
  }

  private buildProjectsListQuery(cursor: string | null): TWorkspaceProjectsListQuery {
    const query: TWorkspaceProjectsListQuery = {
      per_page: projectsListPageSize(),
    };
    const trimmed = this.searchQuery.trim();
    if (trimmed) query.q = trimmed;
    if (this.stateFilter.length > 0) query.state = [...this.stateFilter];
    if (this.categoryFilter.length > 0) query.category = [...this.categoryFilter];
    if (this.leadFilter.length > 0) query.lead = [...this.leadFilter];
    if (this.deliveryTeamsFilter.length > 0) query.delivery_teams = [...this.deliveryTeamsFilter];
    if (this.homeTeamFilter.length > 0) query.home_team = [...this.homeTeamFilter];
    if (this.clientTeamsFilter.length > 0) query.client_teams = [...this.clientTeamsFilter];
    if (this.programsFilter.length > 0) query.programs = [...this.programsFilter];
    if (this.goalsFilter.length > 0) query.goals = [...this.goalsFilter];
    if (this.processFilter.length > 0) query.process = [...this.processFilter];
    if (this.stageFilter.length > 0) query.stage = [...this.stageFilter];
    if (this.archivedFilter) query.archived = true;
    const customField = serializeCustomFieldFilters(this.customFieldFilters.entries());
    if (customField.length > 0) query.custom_field = customField;
    // Single-column server-side sort: map the TanStack sort entry to the
    // backend's whitelisted token (column id, prefixed with "-" for desc).
    const sort = this.sorting[0];
    if (sort) query.order_by = `${sort.desc ? "-" : ""}${sort.id}`;
    // Group-by orders rows so a group's members stay contiguous across pages;
    // it takes precedence over `order_by` on the server.
    if (this.groupBy !== "none") query.group_by = this.groupBy;
    if (cursor) query.cursor = cursor;
    return query;
  }

  /**
   * Export the projects list as CSV. Scoped to the current filters/sort;
   * ``ids`` (selected rows) restrict the export when present. Returns the raw
   * Response so the caller can stream the file (200) or poll the token (202).
   */
  exportProjectsCsv(slug: string, ids?: string[], columns?: string[]): Promise<Response> {
    const body: { ids?: string[]; columns?: string[] } = {};
    if (ids && ids.length > 0) body.ids = ids;
    if (columns && columns.length > 0) body.columns = columns;
    return this.rootStore.projectService.exportProjects(
      slug,
      body,
      this.buildProjectsListQuery(null),
    );
  }

  /** Poll an async export by token until its presigned URL is ready (or null). */
  pollExport(slug: string, token: string): Promise<string | null> {
    return pollExportStatus(token, (tok) =>
      this.rootStore.projectService.getExportStatus(slug, tok),
    );
  }

  async loadPage(slug: string, cursor: string | null = null): Promise<void> {
    const query = this.buildProjectsListQuery(cursor);
    const key = `${slug}:${JSON.stringify(query)}`;
    const existing = this.inflightPage.get(key);
    if (existing) {
      await existing;
      return;
    }
    const token = (this.loadTokenBySlug.get(slug) ?? 0) + 1;
    this.loadTokenBySlug.set(slug, token);
    const isLatest = () => this.loadTokenBySlug.get(slug) === token;
    const promise = (async () => {
      runInAction(() => {
        this.isLoading = true;
        this.loadError = null;
        if (!cursor) this.layoutRowLimit = this.layoutBatchRows;
      });
      this.pageLoadingSlugs.add(slug);
      try {
        const page = await this.rootStore.projectService.list(slug, query);
        const state = projectsEnvelopeToPageState(page);
        runInAction(() => {
          // Drop the response if a newer load for this slug has superseded it.
          if (!isLatest()) return;
          this.pageBySlug.set(slug, state);
          this.cursorBySlug.set(slug, cursor);
          if (!cursor) this.firstPageFailed.delete(slug);
          // Replace the workspace cache when this is a first-page load
          // (no cursor); subsequent pages append in `loadMore`.
          if (!cursor) {
            this.projectsByWorkspace.set(slug, [...state.results]);
            // One page, not the bank — see `fullyLoadedWorkspaces`.
            this.fullyLoadedWorkspaces.delete(slug);
          }
        });
        return state;
      } catch (err) {
        runInAction(() => {
          if (!isLatest()) return;
          this.loadError = err instanceof Error ? err : new Error(String(err));
          if (!cursor) this.firstPageFailed.add(slug);
        });
        throw err;
      } finally {
        runInAction(() => {
          if (!isLatest()) return;
          this.isLoading = false;
          this.pageLoadingSlugs.delete(slug);
        });
      }
    })().finally(() => {
      this.inflightPage.delete(key);
    });
    this.inflightPage.set(key, promise);
    await promise;
  }

  async loadMore(slug: string): Promise<void> {
    const page = this.pageBySlug.get(slug);
    // No page yet (initial load failed) — the DataTable error-empty "Try again"
    // routes the retry here, so re-run the first page rather than no-opping.
    if (!page) {
      await this.loadPage(slug, null);
      return;
    }
    if (!page.nextCursor) return;
    const inflight = this.inflightLoadMore.get(slug);
    if (inflight) return inflight;
    // Append is only valid while no newer first-page load supersedes it: if a
    // search/loader load lands meanwhile, dropping this stale append keeps the
    // fresh page intact. (#1809)
    const loadToken = this.loadTokenBySlug.get(slug);
    runInAction(() => {
      this.isLoadingMore = true;
      this.loadError = null;
    });
    const promise = (async () => {
      try {
        const query = this.buildProjectsListQuery(page.nextCursor);
        const next = await this.rootStore.projectService.list(slug, query);
        const state = projectsEnvelopeToPageState(next);
        runInAction(() => {
          if (this.loadTokenBySlug.get(slug) !== loadToken) return;
          // The token only says no load STARTED since; a first-page load already in
          // flight when this one began keeps the same token, and can land first.
          // Append only to the page this request extends, or the stale rows plus a
          // page read with a foreign cursor replace the fresh one: a project twice
          // and another missing on the Board (NG-2676).
          if (this.pageBySlug.get(slug) !== page) return;
          const loadedIds = new Set(page.results.map((row) => row.id));
          const fresh = state.results.filter((row) => !loadedIds.has(row.id));
          this.pageBySlug.set(slug, {
            ...state,
            // Keep the full appended row list so the table sees [...existing, ...new].
            results: [...page.results, ...fresh],
          });
          const existing = this.projectsByWorkspace.get(slug) ?? [];
          const cachedIds = new Set(existing.map((row) => row.id));
          const merged = [...existing, ...fresh.filter((row) => !cachedIds.has(row.id))];
          this.projectsByWorkspace.set(slug, merged);
          // Still the table's pages rather than the bank — see
          // `fullyLoadedWorkspaces`. Appending does not make a partial list whole.
          this.fullyLoadedWorkspaces.delete(slug);
        });
      } catch (err) {
        runInAction(() => {
          this.loadError = err instanceof Error ? err : new Error(String(err));
        });
        throw err;
      }
    })().finally(() => {
      this.inflightLoadMore.delete(slug);
      runInAction(() => {
        this.isLoadingMore = false;
      });
    });
    this.inflightLoadMore.set(slug, promise);
    return promise;
  }

  /** Rows one `PROJECTS_LAYOUT_AUTOLOAD_PAGES` batch holds at the current page size. */
  get layoutBatchRows(): number {
    return PROJECTS_LAYOUT_AUTOLOAD_PAGES * projectsListPageSize();
  }

  /**
   * Load the pages after the first, one at a time, until every matching
   * project is loaded or `layoutRowLimit` rows are. The Board, Timeline and
   * Calendar layouts call this; the Table keeps its scroll sentinel.
   *
   * It walks the same cursor chain `loadMore` appends to, so switching from the
   * Table to the Board reuses the pages already loaded and switching back keeps
   * them, with no refetch. Each step reads the CURRENT page state, so a filter
   * change mid-walk (which replaces the first page) is followed rather than
   * fought: the walk stops while a first-page load is in flight, and the
   * layout restarts it once that page lands.
   *
   * Stops on the first failure: `loadMore` has already set `loadError`, which
   * the layouts' notice shows with a "Try again". Never rejects.
   */
  loadRemainingPages(slug: string): Promise<void> {
    this.remainingWanted.add(slug);
    const inflight = this.inflightRemaining.get(slug);
    if (inflight) return inflight;
    // Read inside an action: each step after an `await` runs outside the one
    // that started it, and `observableRequiresReaction` warns on a bare read.
    const nextStep = () =>
      runInAction(() => {
        const page = this.pageBySlug.get(slug);
        return {
          wanted:
            this.remainingWanted.has(slug) &&
            // A first-page load in flight is about to replace this chain; an
            // append now would be dropped, so wait to be restarted after it.
            // `isLoading` alone is not that signal: a project-bank walk clears it
            // in its own `finally` while the list's first page is still loading
            // (NG-2676), so also ask whether the latest list load has settled.
            !this.isLoading &&
            !this.firstPageInFlight(slug) &&
            !!page?.nextCursor &&
            page.results.length < this.layoutRowLimit,
          loaded: page?.results.length ?? 0,
          total: page?.totalCount ?? 0,
        };
      });
    const promise = (async () => {
      while (nextStep().wanted) {
        try {
          await this.loadMore(slug);
        } catch (err) {
          const { loaded, total } = nextStep();
          console.error("[projects] loading the remaining pages failed", {
            slug,
            loaded,
            total,
            err,
          });
          return;
        }
      }
    })().finally(() => {
      this.inflightRemaining.delete(slug);
    });
    this.inflightRemaining.set(slug, promise);
    return promise;
  }

  /** The latest list load for `slug` is still in flight. */
  private firstPageInFlight(slug: string): boolean {
    return this.pageLoadingSlugs.has(slug);
  }

  /** Stop `loadRemainingPages` after the request in flight, e.g. on a switch to the Table. */
  stopLoadingRemainingPages(slug: string): void {
    this.remainingWanted.delete(slug);
  }

  /**
   * "Try again" on the layouts' notice. A failed first page is reloaded, since
   * whatever rows `pagination` still holds belong to the query before it; a
   * failed later page resumes the walk from the last page that loaded. Never
   * rejects: a failure lands in `loadError`, which the notice shows again.
   */
  async retryLayoutLoad(slug: string): Promise<void> {
    if (this.firstPageFailed.has(slug) || !this.pageBySlug.has(slug)) {
      try {
        await this.loadPage(slug, null);
      } catch (err) {
        console.error("[projects] retrying the first page failed", { slug, err });
        return;
      }
    }
    await this.loadRemainingPages(slug);
  }

  /** "Load more" on the layouts' notice: allow one more batch, then keep walking. */
  loadNextLayoutBatch(slug: string): Promise<void> {
    this.layoutRowLimit += this.layoutBatchRows;
    return this.loadRemainingPages(slug);
  }

  async refreshCurrentPage(slug: string): Promise<void> {
    if (!this.pageBySlug.has(slug)) return;
    // Always reload from page 1 so the full accumulated list is replaced
    // consistently, not just the last-loaded page.
    await this.loadPage(slug, null);
  }

  get currentWorkspaceProjects(): TPartialProject[] {
    const slug = this.rootStore.router.workspaceSlug;
    if (!slug) return [];
    return this.projectsByWorkspace.get(slug) ?? [];
  }

  get currentProject(): TPartialProject | undefined {
    const id = this.rootStore.router.projectId;
    if (!id) return undefined;
    // Prefer the detail map (populated by ``fetchProjectDetail`` on the
    // overview route + create flows) over the workspace list cache. The list
    // cache is only refreshed via ``fetchWorkspaceProjects`` and can lag
    // behind a freshly created/template-spawned project, causing downstream
    // consumers (e.g. the issues page that builds ``/tasks/<identifier>-N``
    // links) to fall back to placeholder identifiers and 404.
    const detail = this.projectDetailMap.get(id);
    if (detail) return detail;
    return this.currentWorkspaceProjects.find((p) => p.id === id);
  }

  /** Look up a project by id, preferring the detail map (V3 fields) over the list cache. */
  getProjectById(workspaceSlug: string, projectId: string): TProject | TPartialProject | undefined {
    const detail = this.projectDetailMap.get(projectId);
    if (detail) return detail;
    return (this.projectsByWorkspace.get(workspaceSlug) ?? []).find((p) => p.id === projectId);
  }

  // Computed: projects filtered and sorted by current filter state
  get filteredProjects(): TPartialProject[] {
    let projects = [...this.currentWorkspaceProjects];

    // Filter by search
    if (this.filters.search) {
      const q = this.filters.search.toLowerCase();
      projects = projects.filter(
        (p) => p.name.toLowerCase().includes(q) || p.identifier.toLowerCase().includes(q),
      );
    }

    // Filter by privacy
    if (this.filters.privacy !== null) {
      projects = projects.filter((p) => p.privacy === this.filters.privacy);
    }

    // Sort
    projects.sort((a, b) => {
      const key = this.filters.sortBy;
      const order = this.filters.sortOrder === "asc" ? 1 : -1;
      if (key === "name") return a.name.localeCompare(b.name) * order;
      if (key === "sort_order") return ((a.sort_order ?? 0) - (b.sort_order ?? 0)) * order;
      // updated_at / created_at
      const aVal = String((a as unknown as Record<string, unknown>)[key] ?? "");
      const bVal = String((b as unknown as Record<string, unknown>)[key] ?? "");
      return aVal.localeCompare(bVal) * order;
    });

    return projects;
  }

  // Joined projects (user is a member)
  get joinedProjects(): TPartialProject[] {
    return this.currentWorkspaceProjects.filter((p) => p.is_member === true);
  }

  setFilter<K extends keyof TProjectFilters>(key: K, value: TProjectFilters[K]): void {
    this.filters[key] = value;
  }

  resetFilters(): void {
    this.filters = { search: "", privacy: null, sortBy: "sort_order", sortOrder: "asc" };
  }

  hydrateProjectDetail(slug: string, project: TProject): void {
    runInAction(() => {
      // Claimed like a write, so a detail GET already in flight cannot land on
      // top of the detail this hands in.
      this.patchCachedDetail(slug, project.id, project, { seed: true });
      this.projectDetailMap.set(project.id, project);
      const existing = this.projectsByWorkspace.get(slug) ?? [];
      if (!existing.find((p) => p.id === project.id)) {
        this.projectsByWorkspace.set(slug, [...existing, project as TPartialProject]);
      }
    });
  }

  /** Cached "can the caller create a project?" verdict for a workspace, or
   *  `undefined` until {@link fetchCanCreate} has answered. Callers gate the
   *  "New Project" button on `=== true` so it stays hidden while unanswered
   *  (avoids a flash) and when the caller cannot create. */
  getCanCreate(workspaceSlug: string): boolean | undefined {
    return this.canCreateByWorkspace.get(workspaceSlug);
  }

  /** Probe whether the caller can create a project in this workspace and cache
   *  the verdict. This is the untargeted `project.create` capability check the
   *  can-endpoint answers as "could I create under some team I lead, or at the
   *  workspace scope?" (NG-2176): the button renders before any team is picked,
   *  so there is no `team_id`. Callers holding `project.create` in the workspace
   *  array already know the answer and skip the probe. */
  async fetchCanCreate(workspaceSlug: string): Promise<boolean> {
    const [result] = await this.rootStore.permissionService.can(workspaceSlug, [
      { action: "project.create" },
    ]);
    const can = result?.can ?? false;
    runInAction(() => {
      this.canCreateByWorkspace.set(workspaceSlug, can);
    });
    return can;
  }

  /**
   * Every write below takes the `source` of the surface that made it, for its
   * analytics event. It defaults to `page`, the project's own page; any other
   * surface passes its own. Each event fires after the write succeeds, never on
   * failure.
   */
  async createProject(
    slug: string,
    data: Partial<TProject>,
    source: AnalyticsSource = "page",
  ): Promise<TProject> {
    const payload: Partial<TProject> = {
      ...data,
      module_view: true,
      cycle_view: false,
      issue_views_view: true,
      page_view: true,
      inbox_view: false,
    };

    const project = await this.rootStore.projectService.createProject(slug, payload);
    runInAction(() => {
      this.markBankWrite(slug);
      this.patchCachedDetail(slug, project.id, project, { seed: true });
      const existing = this.projectsByWorkspace.get(slug) ?? [];
      this.projectsByWorkspace.set(slug, [...existing, project as TPartialProject]);
      this.projectDetailMap.set(project.id, project);
    });
    track("project_created", { project_id: project.id, source });
    return project;
  }

  /**
   * Fetch all projects in the workspace by walking the paginated workspace
   * endpoint via `ProjectService.fetchAll`.
   */
  async fetchProjects(slug: string): Promise<TPartialProject[]> {
    const seqAtRequest = this.bankWriteSeq.get(slug) ?? 0;
    this.isLoading = true;
    try {
      const projects = await this.rootStore.projectService.fetchAll(slug);
      runInAction(() => {
        this.projectsByWorkspace.set(slug, projects);
        this.fullyLoadedWorkspaces.add(slug);
      });
      // A forced walk is the freshest bank there is: with the `query-cache`
      // flag on, it fills the cached bank (R2) too, so the next reference read
      // is served from it. Not when a write landed while it walked, since the
      // walk may have read the list before the write.
      if (this.queryCacheEnabled && (this.bankWriteSeq.get(slug) ?? 0) === seqAtRequest) {
        this.primeCachedBank(slug, projects);
      }
      return projects;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  /**
   * The workspace's full project bank, fetched at most once per slug — a cache
   * hit returns immediately and concurrent callers share one walk. Mirrors
   * `ProjectStatesStore.ensureStates`, and exists because `fetchProjects` has
   * neither guard: it always re-walks EVERY page of the cursor endpoint, so a
   * caller that only needs "the names of this workspace's projects" (a picker,
   * a filter facet) paid ceil(N/200) sequential requests each time it asked,
   * however recently another surface had filled the same cache.
   *
   * Use this wherever the bank is REFERENCE DATA. Call `fetchProjects` when you
   * specifically want fresh rows — after a create, or on a surface that renders
   * project fields that may have changed underneath it.
   */
  async ensureProjects(slug: string): Promise<TPartialProject[]> {
    if (this.queryCacheEnabled) {
      let scope;
      try {
        scope = scopeFor(slug);
      } catch (error) {
        if (!(error instanceof ScopeNotReadyError)) throw error;
        // The session has not said who is signed in, so there is no key to
        // cache under. Read the way the flag-off path does.
        console.warn("[ProjectStore] bank read before the session resolved; not cached", {
          slug,
        });
        return this.ensureProjectsUncached(slug);
      }
      return this.readCachedBank(slug, scope);
    }
    return this.ensureProjectsUncached(slug);
  }

  /**
   * The bank for a picker that, with the `query-cache` flag off, has always
   * re-walked it on open (the Create Task dialog's Project picker, the program
   * overview's Add projects picker). Flag off, that is still `fetchProjects`,
   * unchanged. Flag on, it is `ensureProjects`: the bank is reference data, and
   * the writers that change it drop R2, so a re-open within its freshness
   * window needs no request.
   */
  loadProjectsForPicker(slug: string): Promise<TPartialProject[]> {
    return this.queryCacheEnabled ? this.ensureProjects(slug) : this.fetchProjects(slug);
  }

  /** `ensureProjects` with the `query-cache` flag off: the store's own cache. */
  private async ensureProjectsUncached(slug: string): Promise<TPartialProject[]> {
    // A populated cache is NOT enough — `loadPage` and `loadMore` put the
    // paginated table's rows in the same slot, so a reader who visited the
    // Projects list first would otherwise be handed one page as the whole bank
    // and a filter built from it would silently omit projects.
    const cached = this.projectsByWorkspace.get(slug);
    if (cached && this.fullyLoadedWorkspaces.has(slug)) return cached;
    const inflight = this.inflightAllProjects.get(slug);
    if (inflight) return inflight;
    const p = this.fetchProjects(slug).finally(() => {
      this.inflightAllProjects.delete(slug);
    });
    this.inflightAllProjects.set(slug, p);
    return p;
  }

  /**
   * The bank (R2, `app/queries/projects.ts`) for `ensureProjects` with the
   * `query-cache` flag on. A fresh entry answers with no request; concurrent
   * callers share one walk; the answer is projected into `projectsByWorkspace`,
   * which every reader of the list still renders from. `loadPage` and
   * `loadMore` writing a table page into that same slot does not touch R2, so
   * the next reference read puts the whole bank back, as the uncached path's
   * re-walk does.
   */
  private async readCachedBank(
    slug: string,
    scope: ReturnType<typeof scopeFor>,
  ): Promise<TPartialProject[]> {
    const seqAtRequest = this.bankWriteSeq.get(slug) ?? 0;
    const client = getQueryClient();
    const query = projectBankQuery(this.rootStore.projectService, scope);
    // Only a walk shows the list's loading state, as on the uncached path: a
    // fresh entry answers at once and must not flash it.
    const entry = client.getQueryCache().find({ queryKey: query.queryKey });
    const walks =
      entry?.state.data === undefined || entry.isStaleByTime(FRESHNESS.reference.staleTime);
    if (walks) runInAction(() => (this.isLoading = true));
    let projects: TPartialProject[];
    try {
      projects = await client.fetchQuery(query);
    } catch (error) {
      // A write landed while this walk was in flight and removed the entry it
      // was filling (`markBankWrite`), which cancels it. The answer would have
      // predated the write anyway: read again, after it.
      if (isCancelledError(error) && (this.bankWriteSeq.get(slug) ?? 0) !== seqAtRequest) {
        return this.readCachedBank(slug, scope);
      }
      throw error;
    } finally {
      if (walks) runInAction(() => (this.isLoading = false));
    }
    if ((this.bankWriteSeq.get(slug) ?? 0) !== seqAtRequest) {
      return this.readCachedBank(slug, scope);
    }
    runInAction(() => {
      this.projectsByWorkspace.set(slug, projects);
      this.fullyLoadedWorkspaces.add(slug);
    });
    return projects;
  }

  /**
   * After a bulk write: drop the bank (R2), and the detail (R1) of every
   * project it may have changed. That is the picked ids for an `included`
   * selection; for `all` / `all_but_excluded` the server resolves the set from
   * filters, so every project of the workspace goes.
   */
  private markBulkWrite(
    slug: string,
    selection: TBulkSelection<TWorkspaceProjectsListQuery>,
  ): void {
    this.markBankWrite(slug);
    if (selection.mode === "included") {
      for (const id of selection.ids ?? []) this.markDetailWrite(id);
      return;
    }
    for (const id of this.projectDetailMap.keys()) this.claimDetail(id);
    if (!this.queryCacheEnabled) return;
    peekQueryClient()?.removeQueries({
      // `[slug, uid, 'project', …]`: every project entry in the workspace.
      predicate: ({ queryKey }) => queryKey[0] === slug && queryKey[2] === "project",
    });
  }

  /** Bump the bank guard, so a bank walk already in flight is read again. */
  private claimBank(slug: string): void {
    this.bankWriteSeq.set(slug, (this.bankWriteSeq.get(slug) ?? 0) + 1);
  }

  /**
   * After a write that changed one row's fields, not which projects are in the
   * list: with the `query-cache` flag on, merge `patch` into that row of each
   * cached bank (R2) for `slug`, rather than dropping the bank. A walk already
   * in flight is cancelled first, so it cannot land on top of the patch; a bank
   * with nothing cached yet is dropped, there being no list to patch.
   */
  private patchCachedBankRow(
    slug: string,
    projectId: string,
    patch: Partial<TPartialProject>,
  ): void {
    this.claimBank(slug);
    if (!this.queryCacheEnabled) return;
    const client = peekQueryClient();
    if (!client) return;
    const banks = client.getQueryCache().findAll({
      // `[slug, uid, 'projects', 'bank', {archived}]`
      predicate: ({ queryKey }) =>
        queryKey[0] === slug && queryKey[2] === "projects" && queryKey[3] === "bank",
    });
    for (const bank of banks) {
      void client.cancelQueries({ queryKey: bank.queryKey, exact: true });
      const rows = bank.state.data as TPartialProject[] | undefined;
      if (!rows) {
        client.removeQueries({ queryKey: bank.queryKey, exact: true });
        continue;
      }
      client.setQueryData<TPartialProject[]>(
        bank.queryKey,
        rows.map((row) => (row.id === projectId ? { ...row, ...patch } : row)),
      );
    }
  }

  /** Put a freshly walked bank into R2, when there is a principal to key it under. */
  private primeCachedBank(slug: string, projects: TPartialProject[]): void {
    let scope;
    try {
      scope = scopeFor(slug);
    } catch (error) {
      if (!(error instanceof ScopeNotReadyError)) throw error;
      return;
    }
    getQueryClient().setQueryData(projectBankKey(scope), projects);
  }

  /**
   * Record a write that changed `slug`'s project list. With the `query-cache`
   * flag on, also drop the workspace's cached bank (R2, active and archived,
   * for whichever principal holds it), so the next reference read walks again
   * rather than putting back a list from before the write. Dropped rather than
   * patched, as `markDetailWrite` does for R1: patching is a later step of
   * NG-2543.
   */
  private markBankWrite(slug: string): void {
    this.claimBank(slug);
    if (!this.queryCacheEnabled) return;
    peekQueryClient()?.removeQueries({
      // `[slug, uid, 'projects', 'bank', {archived}]`
      predicate: ({ queryKey }) =>
        queryKey[0] === slug && queryKey[2] === "projects" && queryKey[3] === "bank",
    });
  }

  /**
   * A picker that renders the bank is open: Create Task's Project picker, or a
   * program's Add projects (NG-2666). While one is, a pushed hint re-reads the
   * bank so the picker shows another user's change with no reopen. Returns
   * the release, which the picker calls when it closes.
   */
  holdBankOpen(slug: string): () => void {
    this.bankHolds.set(slug, (this.bankHolds.get(slug) ?? 0) + 1);
    let released = false;
    return () => {
      if (released) return;
      released = true;
      const left = (this.bankHolds.get(slug) ?? 1) - 1;
      if (left > 0) {
        this.bankHolds.set(slug, left);
        return;
      }
      this.bankHolds.delete(slug);
      window.clearTimeout(this.bankRereadTimers.get(slug));
      this.bankRereadTimers.delete(slug);
    };
  }

  /**
   * Another user changed the workspace's project list: a pushed hint
   * (NG-2666). Treated as this tab's own writes are (`markBankWrite`): the
   * cached bank (R2) is dropped, so the next read walks, and a walk already in
   * flight is cancelled and read again rather than settling an answer from
   * before the change. Merely invalidating would not do that, since the
   * walk's success clears the mark, and a burst sends a trailing hint about a
   * second after the first, while a walk is likely in flight.
   *
   * With no picker open, that is all. With one open, read the bank again
   * after a random 0–2 s, so the workspace's tabs don't walk in the same
   * instant, and one pending re-read covers a burst. A hidden tab waits for
   * its next use. With `query-cache` off there is no cached bank to drop.
   */
  markBankChangedElsewhere(slug: string): void {
    if (!this.queryCacheEnabled) return;
    this.markBankWrite(slug);
    this.scheduleBankReread(slug);
  }

  private scheduleBankReread(slug: string): void {
    if (!this.bankHolds.has(slug)) return;
    if (this.bankRereadTimers.has(slug)) return;
    const timer = window.setTimeout(() => {
      this.bankRereadTimers.delete(slug);
      if (!this.bankHolds.has(slug)) return;
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      void this.ensureProjects(slug).catch((error: unknown) => {
        console.warn(
          "[ProjectStore] re-reading the bank after a pushed hint failed",
          { slug },
          error,
        );
      });
    }, Math.random() * BANK_REREAD_JITTER_MS);
    this.bankRereadTimers.set(slug, timer);
  }

  /** Fetch a single page for popover/picker UIs (dash-feedback PR2 item 9).
   *
   *  Distinct from ``fetchProjects`` (which walks every page) and
   *  ``loadPage`` (which seeds the workspace-projects table cache). The
   *  result is returned directly so consumers can hold a local React state
   *  without polluting the workspace cache or the page cursors. The
   *  workspace-projects detail cache IS warmed so navigations into a
   *  picker-selected project still see the row. */
  async searchPickerProjects(
    slug: string,
    options: { q?: string; perPage?: number } = {},
  ): Promise<TWorkspaceProjectsPage> {
    const page = await this.rootStore.projectService.list(slug, {
      q: options.q?.trim() || undefined,
      per_page: options.perPage ?? 10,
    });
    runInAction(() => {
      // Keep the workspace cache warm so other readers (e.g. issue panels
      // resolving a project name post-selection) don't re-fetch. We merge,
      // never replace, because this page is a search-scoped subset.
      const cache = this.projectsByWorkspace.get(slug) ?? [];
      const byId = new Map(cache.map((p) => [p.id, p] as const));
      for (const row of page.results) byId.set(row.id, row);
      this.projectsByWorkspace.set(slug, [...byId.values()]);
    });
    return page;
  }

  /**
   * Bump the guard so any detail GET already in flight is discarded. With the
   * `query-cache` flag on, also drop the project's cached detail (R1): every
   * write the store applies to an existing project's detail passes through
   * here, and a loader reading R1 afterwards must not be handed a detail older
   * than the write.
   *
   * This is the drop, for a write with no project left to show (a delete, a
   * denial, a failed write being rolled back): R1 and every entry keyed under
   * the project go. A write that has the new state patches instead, through
   * `patchCachedDetail`.
   */
  /**
   * A write outside this store changed what the project's detail shows: its
   * members, and with them `project_leads` and `is_member`. Discard any detail
   * GET already in flight and, with `query-cache` on, the cached detail (R1),
   * so the next loader reads it again. With the flag off every loader reads it
   * again anyway, so only the in-flight guard matters there.
   */
  markDetailChanged(projectId: string): void {
    this.markDetailWrite(projectId);
  }

  private markDetailWrite(projectId: string): void {
    this.claimDetail(projectId);
    if (!this.queryCacheEnabled) return;
    peekQueryClient()?.removeQueries({
      // `[slug, uid, 'project', id, …]`: R1 and the sub-resources under it, in
      // whichever workspace and for whichever principal holds them, since a
      // project id is unique.
      predicate: ({ queryKey }) => queryKey[2] === "project" && queryKey[3] === projectId,
    });
  }

  /** Bump the detail guard, so a detail GET already in flight is discarded. */
  private claimDetail(projectId: string): void {
    this.detailWriteSeq.set(projectId, (this.detailWriteSeq.get(projectId) ?? 0) + 1);
  }

  /**
   * After a write that knows the project's new state: with the `query-cache`
   * flag on, put that state in R1 rather than dropping it, so the next loader
   * is served the write's answer with no request. A read of R1 already in
   * flight is cancelled first: it may have been answered before the write
   * committed, and landing after the patch it would overwrite it.
   *
   * `patch` is merged into the cached detail. Only a write whose response is a
   * whole detail (`seed`) creates an entry that was not there.
   */
  private patchCachedDetail(
    slug: string,
    projectId: string,
    patch: Partial<TProject>,
    { seed = false }: { seed?: boolean } = {},
  ): void {
    this.claimDetail(projectId);
    if (!this.queryCacheEnabled) return;
    const client = peekQueryClient();
    if (!client) return;
    let scope;
    try {
      scope = scopeFor(slug);
    } catch (error) {
      if (!(error instanceof ScopeNotReadyError)) throw error;
      // No principal yet, so nothing is cached under one.
      return;
    }
    const key = projectDetailKey(scope, projectId);
    void client.cancelQueries({ queryKey: key, exact: true });
    client.setQueryData<TProject>(key, (cached) =>
      cached ? { ...cached, ...patch } : seed ? (patch as TProject) : undefined,
    );
  }

  private get queryCacheEnabled(): boolean {
    return this.rootStore.featureFlags.isEnabled(QUERY_CACHE_FLAG);
  }

  /**
   * The project detail a page needs on arrival: what the project tab layout,
   * the overview and the project wrapper read. With the `query-cache` flag off
   * this is `fetchProjectDetail`, unchanged. With it on, the read goes through
   * the cached detail (R1, `app/queries/projects.ts`), so the loaders of one
   * journey share one entry and re-entering a tab within its freshness window
   * makes no request; the answer is then projected into `projectDetailMap`,
   * which every reader of the detail still renders from.
   *
   * Use `fetchProjectDetail` for a refetch after a write, which must not be
   * served from cache.
   */
  loadProjectDetail(slug: string, projectId: string): Promise<TProject> {
    if (!this.queryCacheEnabled) return this.fetchProjectDetail(slug, projectId);
    let scope;
    try {
      scope = scopeFor(slug);
    } catch (error) {
      if (!(error instanceof ScopeNotReadyError)) throw error;
      // The session has not said who is signed in, so there is no key to
      // cache under. Read the way the flag-off path does.
      console.warn("[ProjectStore] detail read before the session resolved; not cached", {
        slug,
        projectId,
      });
      return this.fetchProjectDetail(slug, projectId);
    }
    return this.readCachedProjectDetail(slug, projectId, scope);
  }

  private async readCachedProjectDetail(
    slug: string,
    projectId: string,
    scope: ReturnType<typeof scopeFor>,
  ): Promise<TProject> {
    const seqAtRequest = this.detailWriteSeq.get(projectId) ?? 0;
    const client = getQueryClient();
    let project: TProject;
    try {
      project = await client.fetchQuery(
        projectDetailQuery(this.rootStore.projectService, scope, projectId),
      );
    } catch (error) {
      // A write landed while this read was in flight and cancelled it, either
      // patching the entry with its answer (`patchCachedDetail`) or dropping it
      // (`markDetailWrite`). The answer would have predated the write anyway:
      // read again, after it, which a patch serves with no request.
      if (isCancelledError(error) && (this.detailWriteSeq.get(projectId) ?? 0) !== seqAtRequest) {
        return this.readCachedProjectDetail(slug, projectId, scope);
      }
      // A 403/404 means this user cannot read the project: nothing cached for
      // it may be served again. The caller decides what the user sees.
      dropOnDenial(client, scope, error, "project", projectId);
      throw error;
    }
    runInAction(() => {
      if ((this.detailWriteSeq.get(projectId) ?? 0) !== seqAtRequest) return;
      this.applyFetchedDetail(slug, project);
    });
    return project;
  }

  /** Put a fetched detail in the detail map and merge it into its list row. */
  private applyFetchedDetail(slug: string, project: TProject): void {
    this.projectDetailMap.set(project.id, project);
    const list = this.projectsByWorkspace.get(slug);
    if (list) {
      const idx = list.findIndex((p) => p.id === project.id);
      if (idx !== -1) {
        list[idx] = { ...list[idx], ...(project as TPartialProject) };
      }
    }
  }

  /**
   * Fetch a project's detail payload, sharing one request between concurrent
   * callers.
   *
   * One project page load reaches this from seven places — the
   * `project-wrapper` effect, the `project/tabs-layout` and `project/overview`
   * clientLoaders (those three through `loadProjectDetail`, which is this call
   * with the `query-cache` flag off), three `project/settings/*` loaders and the
   * team project-layout — so a single load used to issue the GET two to three times
   * (measured at ~2.4 on production, at 816ms each). They are the same request
   * for the same row, so the second caller waits on the first.
   *
   * A refresh issued AFTER a write must never join a request that started
   * before it: the response would predate the mutation, and returning it is how
   * "my change did not apply" looks to a caller reading the resolved value. The
   * `detailWriteSeq` guard below already protects the CACHE from that; joining
   * is gated on the same counter, so a write between the two calls forces a
   * fresh GET rather than a shared stale one (NG-980).
   */
  fetchProjectDetail(slug: string, projectId: string): Promise<TProject> {
    const key = `${slug}:${projectId}`;
    const seq = this.detailWriteSeq.get(projectId) ?? 0;
    const inflight = this.detailInFlight.get(key);
    if (inflight && inflight.seq === seq) return inflight.promise;
    const promise = this.requestProjectDetail(slug, projectId).finally(() => {
      if (this.detailInFlight.get(key)?.promise === promise) {
        this.detailInFlight.delete(key);
      }
    });
    this.detailInFlight.set(key, { seq, promise });
    return promise;
  }

  private async requestProjectDetail(slug: string, projectId: string): Promise<TProject> {
    const seqAtRequest = this.detailWriteSeq.get(projectId) ?? 0;
    const project = await this.rootStore.projectService.getProject(slug, projectId);
    runInAction(() => {
      // A mutation settled while this was in flight, so this payload predates
      // it. Drop it rather than clobbering the newer state — the mutation
      // already wrote both caches.
      if ((this.detailWriteSeq.get(projectId) ?? 0) !== seqAtRequest) return;
      this.applyFetchedDetail(slug, project);
      if (this.queryCacheEnabled) this.primeCachedDetail(slug, project);
    });
    return project;
  }

  /** A forced refetch is the newest answer there is: keep R1 in step with it. */
  private primeCachedDetail(slug: string, project: TProject): void {
    const client = peekQueryClient();
    if (!client) return;
    try {
      client.setQueryData(projectDetailKey(scopeFor(slug), project.id), project);
    } catch (error) {
      if (!(error instanceof ScopeNotReadyError)) throw error;
      // Nothing is cached before the session resolves, so there is nothing to
      // keep in step.
      console.warn("[ProjectStore] detail refetched before the session resolved; not cached", {
        slug,
        projectId: project.id,
      });
    }
  }

  /** Apply a per-row patch to BOTH the workspace cache and the paginated
   * list cache. The workspace projects page renders from ``pageBySlug``;
   * the team-detail page renders from ``projectsByWorkspace``. Mutating
   * only one leaves the other stale until a full refresh. Caller is
   * responsible for being inside ``runInAction``. */
  private _applyListRowPatch(
    slug: string,
    projectId: string,
    patch: Partial<TPartialProject>,
    // How the cached bank follows: a field edit patches its row; a change to
    // which list the project belongs in (archive, unarchive) drops the bank.
    bank: "patch-row" | "drop" = "patch-row",
  ): void {
    // `total_issues` / `completed_issues` are issue-count annotations the LIST
    // endpoint adds to each row; the project detail/update serializer returns
    // them as 0 when the instance isn't annotated. A per-row patch (e.g. a
    // PATCH response merged after an inline edit) must never overwrite them —
    // they're owned by the list fetch — otherwise the Progress / Open-tasks
    // columns clear on every edit. Drop them from the incoming patch.
    if ("total_issues" in patch || "completed_issues" in patch) {
      const { total_issues: _t, completed_issues: _c, ...rest } = patch as Record<string, unknown>;
      void _t;
      void _c;
      patch = rest as Partial<TPartialProject>;
    }
    if (bank === "drop") this.markBankWrite(slug);
    else this.patchCachedBankRow(slug, projectId, patch);
    const list = this.projectsByWorkspace.get(slug);
    if (list) {
      const idx = list.findIndex((p) => p.id === projectId);
      if (idx !== -1) {
        list[idx] = { ...list[idx], ...patch };
      }
    }
    const page = this.pageBySlug.get(slug);
    if (page) {
      const idx = page.results.findIndex((p) => p.id === projectId);
      if (idx !== -1) {
        // Replace the entry rather than mutate in place so MobX's array
        // observation reliably fires for the rendered table.
        const nextResults = [...page.results];
        nextResults[idx] = { ...nextResults[idx], ...(patch as Partial<TWorkspaceProjectRow>) };
        this.pageBySlug.set(slug, { ...page, results: nextResults });
      }
    }
  }

  /**
   * Drop every cached trace of a project the caller turned out not to be able
   * to read (NG-241). Three caches hold projects — the detail map, the workspace
   * list and the paginated page — and a denial has to clear all three or the
   * list keeps offering a link that dead-ends on the access panel, and the next
   * navigation renders the stale row again instead of re-confirming.
   *
   * Distinct from `_removeListRow`, which is the deletion path: this one also
   * drops the detail entry, because the detail entry is what `ProjectWrapper`
   * treats as proof of access.
   */
  forgetProject(slug: string, projectId: string): void {
    runInAction(() => {
      // Bumped like a mutation: this eviction is the proof-of-access decision
      // `ProjectWrapper` reads, so a GET already in flight must not repopulate
      // the entry we just denied.
      this.markDetailWrite(projectId);
      this.projectDetailMap.delete(projectId);
      this._removeListRow(slug, projectId);
    });
  }

  private _removeListRow(slug: string, projectId: string): void {
    this.markBankWrite(slug);
    const list = this.projectsByWorkspace.get(slug);
    if (list) {
      this.projectsByWorkspace.set(
        slug,
        list.filter((p) => p.id !== projectId),
      );
    }
    const page = this.pageBySlug.get(slug);
    if (page) {
      this.pageBySlug.set(slug, {
        ...page,
        results: page.results.filter((p) => p.id !== projectId),
        totalCount: Math.max(0, page.totalCount - 1),
      });
    }
  }

  /**
   * After a write that adds a line to the project's activity feed (an edit, an
   * archive, a restore): with `query-cache` on, refresh the feed if it is on
   * screen, and from NG-2544's feed conversion on, mark a cached one stale
   * (R3, contract §3.1). The feed was never refreshed after a project edit, so
   * with the flag off it still is not: that path stays as it was.
   */
  private refreshActivityAfterWrite(slug: string, projectId: string): void {
    if (!this.queryCacheEnabled) return;
    void this.rootStore.projectActivity.refreshIfCached(slug, projectId);
  }

  async updateProject(
    slug: string,
    projectId: string,
    data: Partial<TProject>,
    source: AnalyticsSource = "page",
  ): Promise<TProject> {
    this.isUpdating = true;
    // Read before the write replaces it: `fields` is what this write changed.
    const before = this.getProjectById(slug, projectId);
    try {
      const updated = await this.rootStore.projectService.updateProject(slug, projectId, data);
      runInAction(() => {
        this.patchCachedDetail(slug, updated.id, updated);
        const existingDetail = this.projectDetailMap.get(updated.id);
        this.projectDetailMap.set(updated.id, {
          ...(existingDetail ?? ({} as TProject)),
          ...updated,
        });
        this._applyListRowPatch(slug, updated.id, updated as TPartialProject);
      });
      trackProjectWrite(projectId, before, data, source);
      this.refreshActivityAfterWrite(slug, projectId);
      return updated;
    } finally {
      runInAction(() => {
        this.isUpdating = false;
      });
    }
  }

  /**
   * PATCH a project with optimistic UI updates. The detail map + workspace
   * list are mutated synchronously before the request fires so consumers
   * (e.g. the team projects table) re-render with the new value immediately.
   * On failure the prior snapshot is restored and the error is re-thrown so
   * the caller can toast.
   */
  async updateProjectOptimistic(
    slug: string,
    projectId: string,
    data: Partial<TProject>,
    source: AnalyticsSource = "page",
  ): Promise<TProject> {
    const prevDetail = this.projectDetailMap.get(projectId);
    const prevPage = this.pageBySlug.get(slug);
    const prevList = this.projectsByWorkspace.get(slug);
    const prevPageRow = prevPage?.results.find((p) => p.id === projectId);
    const prevListRow = prevList?.find((p) => p.id === projectId);
    const baseline =
      prevDetail ??
      ((prevPageRow ?? prevListRow) ? ({ ...(prevPageRow ?? prevListRow) } as TProject) : null);

    runInAction(() => {
      if (baseline) {
        this.projectDetailMap.set(projectId, { ...baseline, ...data });
      }
      this._applyListRowPatch(slug, projectId, data as Partial<TPartialProject>);
    });

    try {
      const updated = await this.rootStore.projectService.updateProject(slug, projectId, data);
      runInAction(() => {
        // Claim the detail cache: a GET that started before this PATCH must not
        // land on top of the result.
        this.patchCachedDetail(slug, projectId, updated);
        this.projectDetailMap.set(updated.id, {
          ...(this.projectDetailMap.get(updated.id) ?? ({} as TProject)),
          ...updated,
        });
        this._applyListRowPatch(slug, updated.id, updated as TPartialProject);
      });
      trackProjectWrite(projectId, baseline ?? undefined, data, source);
      this.refreshActivityAfterWrite(slug, projectId);
      return updated;
    } catch (error) {
      // Roll back both caches to the pre-mutation snapshot.
      runInAction(() => {
        this.markDetailWrite(projectId);
        if (prevDetail) this.projectDetailMap.set(projectId, prevDetail);
        else this.projectDetailMap.delete(projectId);
        if (prevListRow) {
          this._applyListRowPatch(slug, projectId, prevListRow);
        }
        if (prevPageRow) {
          // _applyListRowPatch merges; force a full restore by writing the
          // original row back to pageBySlug verbatim.
          const page = this.pageBySlug.get(slug);
          if (page) {
            const nextResults = page.results.map((p) => (p.id === projectId ? prevPageRow : p));
            this.pageBySlug.set(slug, { ...page, results: nextResults });
          }
        }
      });
      throw error;
    }
  }

  async unarchiveProject(
    slug: string,
    projectId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    await this.rootStore.projectService.unarchiveProject(slug, projectId);
    track("project_restored", { project_id: projectId, source });
    this.refreshActivityAfterWrite(slug, projectId);
    runInAction(() => {
      this.patchCachedDetail(slug, projectId, { archived_at: null });
      this._applyListRowPatch(slug, projectId, { archived_at: null }, "drop");
      const detail = this.projectDetailMap.get(projectId);
      if (detail) {
        this.projectDetailMap.set(projectId, { ...detail, archived_at: null });
      }
    });
  }

  async archiveProject(
    slug: string,
    projectId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    const response = await this.rootStore.projectService.archiveProject(slug, projectId);
    track("project_archived", { project_id: projectId, source });
    this.refreshActivityAfterWrite(slug, projectId);
    const archivedAt = response.archived_at ?? new Date().toISOString();
    runInAction(() => {
      this.patchCachedDetail(slug, projectId, { archived_at: archivedAt });
      this._applyListRowPatch(slug, projectId, { archived_at: archivedAt }, "drop");
      const detail = this.projectDetailMap.get(projectId);
      if (detail) {
        this.projectDetailMap.set(projectId, { ...detail, archived_at: archivedAt });
      }
    });
  }

  async deleteProject(
    slug: string,
    projectId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    await this.rootStore.projectService.deleteProject(slug, projectId);
    track("project_deleted", { project_id: projectId, source });
    runInAction(() => {
      this.markDetailWrite(projectId);
      this._removeListRow(slug, projectId);
      this.projectDetailMap.delete(projectId);
    });
  }

  /**
   * Delete projects selected from the workspace Projects list via the shared
   * selection-contract endpoint (#2594) — the server authorizes per project
   * and deletes only the subset the actor may delete, reporting the rest as
   * `skipped` rather than failing the whole batch. `onProgress` surfaces
   * processed/total while a large ("all"/"all_but_excluded") selection is
   * handed off to a background job; `included` (the only mode the Projects
   * list currently sends) almost always completes inline.
   */
  async bulkDeleteProjects(
    slug: string,
    selection: TBulkSelection<TWorkspaceProjectsListQuery>,
    onProgress?: (status: TBulkOperationStatus) => void,
    source: AnalyticsSource = "bulk",
  ): Promise<TBulkDeleteResult> {
    const result = await runBulkDelete(
      () => this.rootStore.projectService.bulkDeleteProjects(slug, selection),
      {
        poll: (token) => this.rootStore.projectService.getBulkOperationStatus(slug, token),
        onProgress,
      },
    );
    if (selection.mode === "included") {
      runInAction(() => {
        for (const id of selection.ids ?? []) {
          this.markDetailWrite(id);
          this._removeListRow(slug, id);
          this.projectDetailMap.delete(id);
        }
      });
    }
    // The server may have changed any selected row; the store patches none
    // of them (or, for a delete, only an `included` selection).
    runInAction(() => this.markBulkWrite(slug, selection));
    track("project_deleted", { count: result.deleted, source });
    return result;
  }

  /**
   * Set the status on projects selected from the workspace Projects list via
   * the shared selection-contract endpoint (#2595) — mirrors
   * `bulkDeleteProjects` (#2594): the server authorizes per project and
   * applies the change only to the subset whose workflow allows the direct
   * transition, reporting the rest as `skipped`. No local optimistic patch —
   * the result doesn't say *which* ids were skipped, so the caller reloads
   * the page to true up rather than risk showing the new status on a
   * skipped row.
   */
  async bulkSetStatus(
    slug: string,
    selection: TBulkSelection<TWorkspaceProjectsListQuery>,
    stateId: string,
    onProgress?: (status: TBulkOperationStatus) => void,
    source: AnalyticsSource = "bulk",
  ): Promise<TBulkSetProjectStatusResult | { processed: number; skipped: number }> {
    const result = await runBulkAction<TBulkSetProjectStatusResult>(
      () => this.rootStore.projectService.bulkSetProjectStatus(slug, selection, stateId),
      {
        poll: (token) => this.rootStore.projectService.getBulkOperationStatus(slug, token),
        onProgress,
      },
    );
    // The server may have changed any selected row; the store patches none
    // of them (or, for a delete, only an `included` selection).
    runInAction(() => this.markBulkWrite(slug, selection));
    track("project_status_changed", { to: stateId, count: bulkChangedCount(result), source });
    return result;
  }

  /**
   * Archive projects selected from the workspace Projects list via the
   * shared selection-contract endpoint (#2598) — mirrors `bulkSetStatus`
   * (#2595)/`bulkDeleteProjects` (#2594). No local optimistic patch: the
   * result doesn't say which ids were skipped, so the caller reloads the
   * page rather than risk showing an archived state on a skipped row.
   */
  async bulkArchiveProjects(
    slug: string,
    selection: TBulkSelection<TWorkspaceProjectsListQuery>,
    onProgress?: (status: TBulkOperationStatus) => void,
    source: AnalyticsSource = "bulk",
  ): Promise<TBulkArchiveProjectsResult | { processed: number; skipped: number }> {
    const result = await runBulkAction<TBulkArchiveProjectsResult>(
      () => this.rootStore.projectService.bulkArchiveProjects(slug, selection),
      {
        poll: (token) => this.rootStore.projectService.getBulkOperationStatus(slug, token),
        onProgress,
      },
    );
    // The server may have changed any selected row; the store patches none
    // of them (or, for a delete, only an `included` selection).
    runInAction(() => this.markBulkWrite(slug, selection));
    track("project_archived", { count: bulkChangedCount(result), source });
    return result;
  }

  /**
   * Assign (`assign: true`) or unassign (`false`) one user as project.lead
   * across projects selected from the workspace Projects list via the shared
   * selection-contract endpoint (#2596) — mirrors `bulkArchiveProjects`
   * (#2598)/`bulkSetStatus` (#2595). No local optimistic patch on this store:
   * `BulkLeadsPicker` keeps its own local overlay for instant per-click
   * feedback across a rapid multi-click session, and triggers a page refresh
   * when it closes to true up the shared row data.
   */
  async bulkAssignLead(
    slug: string,
    selection: TBulkSelection<TWorkspaceProjectsListQuery>,
    userId: string,
    assign: boolean,
    onProgress?: (status: TBulkOperationStatus) => void,
    source: AnalyticsSource = "bulk",
  ): Promise<TBulkAssignLeadsResult | { processed: number; skipped: number }> {
    const result = await runBulkAction<TBulkAssignLeadsResult>(
      () => this.rootStore.projectService.bulkAssignProjectLeads(slug, selection, userId, assign),
      {
        poll: (token) => this.rootStore.projectService.getBulkOperationStatus(slug, token),
        onProgress,
      },
    );
    // The server may have changed any selected row; the store patches none
    // of them (or, for a delete, only an `included` selection).
    runInAction(() => this.markBulkWrite(slug, selection));
    track(assign ? "project_lead_assigned" : "project_lead_unassigned", {
      member_id: userId,
      count: bulkChangedCount(result),
      source,
    });
    return result;
  }

  /** Request cancellation of a long-running bulk operation (delete/archive/
   *  set-status/set-leads) by token — see `cancelBulkOperation` on the
   *  service for the endpoint contract. */
  cancelBulkOperation(slug: string, token: string): Promise<{ token: string; status: string }> {
    return this.rootStore.projectService.cancelBulkOperation(slug, token);
  }

  async duplicateProject(
    slug: string,
    projectId: string,
    source: AnalyticsSource = "page",
  ): Promise<TProject> {
    const newProject = await this.rootStore.projectV3Service.duplicate(slug, projectId);
    runInAction(() => {
      this.markBankWrite(slug);
      this.patchCachedDetail(slug, newProject.id, newProject, { seed: true });
      const projects = this.projectsByWorkspace.get(slug);
      if (projects) {
        this.projectsByWorkspace.set(slug, [...projects, newProject]);
      }
      const page = this.pageBySlug.get(slug);
      if (page) {
        this.pageBySlug.set(slug, {
          ...page,
          results: [...page.results, newProject as unknown as TWorkspaceProjectRow],
          totalCount: page.totalCount + 1,
        });
      }
      this.projectDetailMap.set(newProject.id, newProject);
    });
    track("project_duplicated", {
      project_id: newProject.id,
      duplicated_from_id: projectId,
      source,
    });
    return newProject;
  }
}
